import { Fragment } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  parseSummaryMarkdown,
  type InlineSpan,
  type SummaryBlock,
} from '@/lib/visio/summary-markdown';

function Spans({ spans }: { spans: InlineSpan[] }) {
  return (
    <>
      {spans.map((span, i) => {
        if (span.bold) return <strong key={i}>{span.text}</strong>;
        if (span.italic) return <em key={i}>{span.text}</em>;
        return <Fragment key={i}>{span.text}</Fragment>;
      })}
    </>
  );
}

function Block({ block }: { block: SummaryBlock }) {
  switch (block.type) {
    case 'heading':
      return (
        <p
          className={cn(
            'text-ink-900 font-bold',
            block.level === 1 ? 'mt-4 text-base' : 'mt-3 text-sm',
          )}
          role="heading"
          aria-level={block.level + 2}
        >
          <Spans spans={block.spans} />
        </p>
      );
    case 'paragraph':
      return (
        <p className="text-ink-700 text-sm leading-relaxed">
          <Spans spans={block.spans} />
        </p>
      );
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag
          {...(block.ordered ? { start: block.start } : {})}
          className={cn(
            'text-ink-700 ml-5 space-y-1 text-sm leading-relaxed',
            block.ordered ? 'list-decimal' : 'list-disc',
          )}
        >
          {block.items.map((item, i) => (
            <li key={i}>
              <Spans spans={item} />
            </li>
          ))}
        </Tag>
      );
    }
  }
}

/**
 * Rendu du résumé d'un rendez-vous (texte produit par un LLM à partir du
 * dialogue) — construit des éléments React à partir d'un parseur minimal,
 * JAMAIS via `dangerouslySetInnerHTML` : un HTML injecté par le dialogue
 * s'affiche comme du texte, il ne s'exécute pas.
 */
export function SummaryMarkdown({ markdown, className }: { markdown: string; className?: string }) {
  const blocks = parseSummaryMarkdown(markdown);
  return (
    <div className={cn('space-y-2', className)}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}
